import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import PullToRefresh from "@/components/PullToRefresh";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { SlidersHorizontal, X, BellRing } from "lucide-react";

const SIZES = ["XS","S","M","L","XL","XXL","36","37","38","39","40","41","42","43","44","Unique"];
const CONDITIONS = [
  { key: "neuf_etiquette", label: "Neuf avec étiquette" },
  { key: "tres_bon", label: "Très bon" },
  { key: "bon", label: "Bon" },
  { key: "correct", label: "Correct" },
];
const CATEGORIES = [
  { key: "vetements", label: "Vêtements" },
  { key: "chaussures", label: "Chaussures" },
  { key: "sacs", label: "Sacs" },
  { key: "accessoires", label: "Accessoires" },
];

export default function Catalog() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [wishlist, setWishlist] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [sheetOpen, setSheetOpen] = useState(false);

  const filters = useMemo(() => ({
    category: params.get("category") || "",
    q: params.get("q") || "",
    condition: params.get("condition") || "",
    size: params.get("size") || "",
    brand: params.get("brand") || "",
    min_price: params.get("min_price") || "",
    max_price: params.get("max_price") || "",
  }), [params]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const cleaned = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== "" && v !== null));
      const { data } = await api.get("/products", { params: cleaned });
      setProducts(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchProducts(); /* eslint-disable-next-line */ }, [filters]);

  useEffect(() => {
    if (!user) return;
    api.get("/wishlist").then(r => setWishlist(new Set(r.data.map(p => p.product_id)))).catch(()=>{});
  }, [user]);

  const setFilter = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next);
  };

  const clearFilters = () => setParams(new URLSearchParams());

  const onWishlist = async (product) => {
    if (!user) { toast.error("Connecte-toi pour ajouter aux favoris"); return; }
    const has = wishlist.has(product.product_id);
    try {
      if (has) {
        await api.delete(`/wishlist/${product.product_id}`);
        const ns = new Set(wishlist); ns.delete(product.product_id); setWishlist(ns);
      } else {
        await api.post(`/wishlist/${product.product_id}`);
        const ns = new Set(wishlist); ns.add(product.product_id); setWishlist(ns);
      }
    } catch { toast.error("Erreur"); }
  };

  const activeCount = Object.values(filters).filter(v => v).length;

  const saveSearch = async () => {
    if (!user) { toast.error("Connecte-toi pour créer une alerte"); return; }
    try {
      const cleaned = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== "" && v !== null));
      if (cleaned.min_price) cleaned.min_price = parseFloat(cleaned.min_price);
      if (cleaned.max_price) cleaned.max_price = parseFloat(cleaned.max_price);
      await api.post("/saved-searches", cleaned);
      toast.success("Alerte créée 🔔 Tu seras prévenu des nouveaux articles");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <PullToRefresh onRefresh={fetchProducts}>
    <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-6 lg:py-10 fade-in has-bottom-nav" data-testid="catalog-page">
      {/* Header */}
      <div className="mb-6 lg:mb-10">
        <p className="label mb-2">{filters.category ? `Catégorie · ${CATEGORIES.find(c=>c.key===filters.category)?.label}` : "Tout le catalogue"}</p>
        <h1 className="text-3xl lg:text-5xl font-black tracking-tighter">
          {filters.q ? <>« {filters.q} »</> : "Le drop"}
        </h1>
      </div>

      {/* Category quick chips */}
      <div className="flex gap-2 mb-4 lg:mb-6 overflow-x-auto no-scrollbar -mx-4 px-4">
        <button onClick={()=>setFilter("category", "")} className={`pill ${!filters.category ? '' : 'pill-outline'} shrink-0`} data-testid="chip-all">Tout</button>
        {CATEGORIES.map(c => (
          <button key={c.key} onClick={()=>setFilter("category", c.key)} className={`pill ${filters.category===c.key ? 'pill-mint' : 'pill-outline'} shrink-0`} data-testid={`chip-${c.key}`}>
            {c.label}
          </button>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between mb-6 hairline-b pb-3">
        <button onClick={()=>setSheetOpen(true)} className="btn btn-ghost btn-sm" data-testid="filters-toggle">
          <SlidersHorizontal size={14} strokeWidth={2.5} /> Filtres {activeCount > 0 && <span className="ml-1 bg-[color:var(--color-coral)] text-white px-1.5 text-xs font-bold">{activeCount}</span>}
        </button>
        <div className="flex items-center gap-3">
          {activeCount > 0 && (
            <button onClick={saveSearch} className="btn btn-ghost btn-sm" data-testid="save-search-btn">
              <BellRing size={14} strokeWidth={2.5} /> Créer une alerte
            </button>
          )}
          <span className="label">{products.length} article{products.length > 1 ? 's' : ''}</span>
        </div>
      </div>

      {/* Products grid */}
      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="aspect-square bg-[color:var(--color-surface)]"/>
              <div className="h-4 mt-3 w-2/3 bg-[color:var(--color-surface)]"/>
            </div>
          ))}
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-20 border-2 border-dashed border-black" data-testid="empty-catalog">
          <p className="text-3xl lg:text-4xl font-black mb-3">Rien à se mettre 🫠</p>
          <p className="text-sm text-[color:var(--color-text-muted)] mb-6">Essaie d'élargir tes filtres.</p>
          {activeCount > 0 && <button className="btn btn-primary" onClick={clearFilters}>Réinitialiser</button>}
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {products.map(p => <ProductCard key={p.product_id} product={p} onWishlist={onWishlist} isWishlisted={wishlist.has(p.product_id)} />)}
        </div>
      )}

      {/* Filter bottom sheet */}
      {sheetOpen && (
        <>
          <div className="sheet-backdrop" onClick={()=>setSheetOpen(false)} />
          <div className="fixed left-0 right-0 bottom-0 lg:left-auto lg:right-12 lg:bottom-12 lg:w-[420px] bg-white border-2 border-black z-[70] slide-up max-h-[88vh] overflow-y-auto" data-testid="filters-panel">
            <div className="sticky top-0 bg-white hairline-b p-4 flex items-center justify-between">
              <h3 className="text-2xl font-black tracking-tight">Filtres</h3>
              <button onClick={()=>setSheetOpen(false)} className="w-10 h-10 flex items-center justify-center" data-testid="close-filters"><X size={20}/></button>
            </div>
            <div className="p-4 space-y-6">
              <div>
                <p className="label font-bold mb-3">État</p>
                <div className="grid grid-cols-2 gap-2">
                  {CONDITIONS.map(c => (
                    <button key={c.key} onClick={()=>setFilter("condition", filters.condition===c.key?"":c.key)}
                      className={`btn btn-sm ${filters.condition===c.key ? 'btn-primary' : 'btn-ghost'}`}>{c.label}</button>
                  ))}
                </div>
              </div>

              <div>
                <p className="label font-bold mb-3">Taille</p>
                <div className="flex flex-wrap gap-2">
                  {SIZES.map(s => (
                    <button key={s} onClick={()=>setFilter("size", filters.size===s?"":s)}
                      className={`btn btn-sm ${filters.size===s ? 'btn-primary' : 'btn-ghost'}`}>{s}</button>
                  ))}
                </div>
              </div>

              <div>
                <p className="label font-bold mb-3">Marque</p>
                <input className="field" placeholder="Ex: Carhartt" value={filters.brand} onChange={(e)=>setFilter("brand", e.target.value)} data-testid="filter-brand" />
              </div>

              <div>
                <p className="label font-bold mb-3">Prix (€)</p>
                <div className="flex gap-2">
                  <input className="field" placeholder="Min" type="number" value={filters.min_price} onChange={(e)=>setFilter("min_price", e.target.value)} data-testid="filter-min" />
                  <input className="field" placeholder="Max" type="number" value={filters.max_price} onChange={(e)=>setFilter("max_price", e.target.value)} data-testid="filter-max" />
                </div>
              </div>
            </div>
            <div className="sticky bottom-0 bg-white hairline-t p-4 flex gap-3">
              {activeCount > 0 && <button onClick={clearFilters} className="btn btn-ghost flex-1" data-testid="clear-filters">Tout effacer</button>}
              <button onClick={()=>setSheetOpen(false)} className="btn btn-mint flex-1" data-testid="apply-filters">Voir {products.length} articles</button>
            </div>
          </div>
        </>
      )}
    </div>
    </PullToRefresh>
  );
}
